import { defaultSAL2110_R01Dto, SAL2110_R01Dto } from '../Dto_SAL2110_R01';

export type SAL2110_R01GroupType = 'custom' | 'brand' | 'item';

/** 집계 테이블용 rowKey를 `그룹종류:그룹코드` 형식으로 생성한다. */
export const createSAL2110_R01RowKey = (groupType: SAL2110_R01GroupType, groupCd: string): string => {
	return `${groupType}:${groupCd}`;
};

/** 상세 행의 매출액을 읽는다. totalAmt가 없으면 itemAmt를 사용한다. */
const getRowAmt = (row: SAL2110_R01Dto): number => {
	return Number(row.totalAmt) || Number(row.itemAmt) || 0;
};

/** 상세 행을 그룹 코드 기준으로 묶어 매출량·매출액을 합산하고 내림차순 정렬한다. */
const aggregateByGroup = (
	rows: SAL2110_R01Dto[],
	groupType: SAL2110_R01GroupType,
	getGroupCd: (row: SAL2110_R01Dto) => string,
	applyGroupFields: (target: SAL2110_R01Dto, source: SAL2110_R01Dto) => void,
): SAL2110_R01Dto[] => {
	const grouped = rows.reduce((acc, row) => {
		const groupCd = getGroupCd(row);
		if (!groupCd) return acc;

		const rowKey = createSAL2110_R01RowKey(groupType, groupCd);
		if (!acc[rowKey]) {
			const nextRow: SAL2110_R01Dto = {
				...defaultSAL2110_R01Dto,
				...row,
				rowKey,
				salesQty: 0,
				totalAmt: 0,
			};
			applyGroupFields(nextRow, row);
			acc[rowKey] = nextRow;
		}

		acc[rowKey].salesQty += Number(row.salesQty) || 0;
		acc[rowKey].totalAmt += getRowAmt(row);
		return acc;
	}, {} as Record<string, SAL2110_R01Dto>);

	return Object.values(grouped).sort((a, b) => (b.totalAmt || 0) - (a.totalAmt || 0));
};

/** 거래처(customCd) 기준으로 상세 행을 집계해 거래처별 합계 목록을 만든다. */
export const aggregateCustomTotalList = (rows: SAL2110_R01Dto[]): SAL2110_R01Dto[] => {
	return aggregateByGroup(
		rows,
		'custom',
		(row) => row.customCd,
		(target, source) => {
			target.customCd = source.customCd;
			target.customNm = source.customNm;
		},
	);
};

/** 브랜드(brandCd) 기준으로 상세 행을 집계해 브랜드별 합계 목록을 만든다. */
export const aggregateBrandTotalList = (rows: SAL2110_R01Dto[]): SAL2110_R01Dto[] => {
	return aggregateByGroup(
		rows,
		'brand',
		(row) => row.brandCd,
		(target, source) => {
			target.brandCd = source.brandCd;
			target.brandNm = source.brandNm;
		},
	);
};

/** 품목(itemCd) 기준으로 상세 행을 집계해 품목별 합계 목록을 만든다. */
export const aggregateItemTotalList = (rows: SAL2110_R01Dto[]): SAL2110_R01Dto[] => {
	return aggregateByGroup(
		rows,
		'item',
		(row) => row.itemCd,
		(target, source) => {
			target.itemCd = source.itemCd;
			target.itemNm = source.itemNm;
			target.itemSpec = source.itemSpec;
		},
	);
};
